import { css, html, LitElement } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { displayAlert } from 'src/utilities/display-alert.js';
import { t } from 'src/i18n';
import '../../library/editors/elements/d-edit-text.js';
import '../../library/editors/elements/d-edit-textarea.js';

import '../../library/editors/elements/d-select-dropdown.js';
import '../../library/editors/elements/d-select-radio.js';
import '../../library/elements/d-label.js';
import '../../library/elements/d-section.js';
import '../../library/fields/d-expansion.js';
import { notifyError } from '../../utilities/monitoring.js';
import type { SelectDropdownOption } from '../../library/editors/elements/select-dropdown-option';
import type { Subscription } from './subscription';
import { headerViewStyles } from 'src/layout/parts/header-view-styles';

/**
 *
 *
 */
@customElement('d-organization-subscription')
export class DOrganizationSubscription extends LitElement {
  static readonly styles = [
    headerViewStyles,
    css`
      :host {
        display: block;
      }

      .flexBasis600 {
        flex-basis: 600px;
      }

      .flexBasis300 {
        flex-basis: 300px;
      }

      a.textLink {
        color: black;
        display: inline-block;
        border-bottom: 1px solid hsla(0, 0%, 0%, 0.3);
        text-decoration: none;
      }
    `,
  ];
  private get systemVersionOptions(): SelectDropdownOption[] {
    return [
      { value: 'standard', text: t('layout.subscription.systemVersionStandard') },
      { value: 'singleUser', text: t('layout.subscription.systemVersionSimplified') },
    ];
  }
  /**
   * @private
   */
  private get specialTermsOptions(): SelectDropdownOption[] {
    return [
      { value: 'NONE', text: t('layout.subscription.memberNone') },
      { value: 'DNLF', text: 'Den norske legeforening' }, // i18n-exempt: egennavn (foreningsnavn)
      { value: 'NTF', text: 'Den norske tannlegeforening' }, // i18n-exempt: egennavn (foreningsnavn)
      { value: 'NFF', text: 'Norsk Fysioterapeutforbund' },
      { value: 'NKF', text: 'Norsk Kiropraktorforening' },
      { value: 'NMF', text: 'Norsk Manuellterapeutforening' },
      { value: 'NOF', text: 'Norsk Osteopatforbund' },
      { value: 'NPF', text: 'Norsk psykologforening' },
      { value: 'NNF', text: 'Norges Naprapatforbund' },
    ];
  }
  private get subscriptionTypeOptions() {
    return [
      { value: 'EHF', text: t('layout.subscription.invoiceTypeEhf') },
      { value: 'MANUAL', text: t('layout.subscription.invoiceTypePaper') },
    ];
  }
  @query('#ownerEmail')
  ownerEmail!: HTMLSelectElement;
  @property({ type: String })
  token = '';
  @property({ type: Object })
  subscription!: Subscription;
  @property({ type: Object })
  editItem: Subscription = {
    invoiceReceiver: '',
    invoiceAddress: '',
    invoicePostcode: '',
    invoiceLocality: '',
    invoiceReference: '',
    ownerEmail: '',
    specialTerms: 'NONE',
    invoiceSendMethod: 'MANUAL',
    invoiceOrganizationNumber: '',
    singleUser: false,
    requiresSecureLogin: false,
  };
  @property({ type: Object })
  elmaParams = {};
  @property({ type: Number })
  organizationId = 0;
  @property({ type: Array })
  availableOwnerEmployees: SelectDropdownOption[] = [];
  @property({ type: Boolean })
  singleUserVersionAvailable = false;
  @property({ type: Boolean })
  singleUserVersion = false;
  @property({ type: Array })
  administratorEmployeesList: {
    name: string;
    firstName: string;
    lastName: string;
    uuid: string;
    email: string | undefined;
  }[] = [];

  private get systemVersion() {
    return this.editItem.singleUser ? 'singleUser' : 'standard';
  }

  elmaLookup: (q: string) => Promise<{ text: string; value: string }[]> = () => Promise.resolve([]);

  async onUpdateSubscription() {
    const v = await this.elmaLookup(this.editItem.invoiceOrganizationNumber);
    if (!this._stringIsEmpty(this.editItem.invoiceOrganizationNumber) && !this.validInvoiceOrganizationNumber()) {
      displayAlert(t('layout.subscription.alertOrganizationNumberNineDigits'));
    } else if (this.editItem.invoiceSendMethod === 'EHF' && !this.validInvoiceOrganizationNumber()) {
      displayAlert(t('layout.subscription.alertEhfRequiresOrganizationNumber'));
    } else if (this.editItem.invoiceSendMethod === 'EHF' && v.length !== 1) {
      displayAlert(t('layout.subscription.alertEhfRecipientNotInElma'));
    } else if (this._isValid()) {
      this.dispatchEvent(
        new CustomEvent<Subscription>('update-subscription', { composed: true, bubbles: true, detail: this.editItem }),
      );
    } else {
      notifyError(new Error('Invalid subscription ' + JSON.stringify(this.editItem)));
      displayAlert(t('layout.subscription.alertFillAllFields'));
    }
  }

  _stringIsEmpty(value: string) {
    return value ? value.trim().length === 0 : true;
  }

  _isValid() {
    const t = this.editItem;

    return (
      !this._stringIsEmpty(t.ownerEmail) &&
      !this._stringIsEmpty(t.specialTerms) &&
      !this._stringIsEmpty(t.invoiceSendMethod) &&
      !this._stringIsEmpty(t.invoiceReceiver) &&
      !this._stringIsEmpty(t.invoiceAddress) &&
      !this._stringIsEmpty(t.invoicePostcode) &&
      !this._stringIsEmpty(t.invoiceLocality)
    );
  }

  render() {
    return html`
      <div class="settings-section">
        <h2>${t('layout.subscription.heading')}</h2>
        <d-wrap>
          <d-wrap>
            <d-select-dropdown
              label="${t('layout.subscription.administrator')}"
              .value=${this.editItem.ownerEmail}
              .options=${this.availableOwnerEmployees}
              toolbar
              system-content
              class="flexBasis300"
              @value-changed=${this.onOwnerEmailChanged}
            >
            </d-select-dropdown>
            <d-select-dropdown
              field="organization_systemVersion"
              ?disabled=${!this.singleUserVersionAvailable}
              .value=${this.systemVersion}
              .options=${this.systemVersionOptions}
              toolbar
              system-content
              class="flexBasis300"
              @value-changed=${this.onSystemVersionChanged}
            >
            </d-select-dropdown>
          </d-wrap>
          <d-select-dropdown
            label="${t('layout.subscription.memberOf')}"
            .value=${this.editItem.specialTerms}
            .options=${this.specialTermsOptions}
            toolbar
            system-content
            class="flexBasis600"
            @value-changed=${this.onSpecialTermsChanged}
          >
          </d-select-dropdown>
          <d-edit-text
            class="flexBasis600"
            label="${t('layout.subscription.invoiceReceiver')}"
            value="${this.editItem.invoiceReceiver}"
            toolbar
            system-content
            @value-changed=${this.onInvoiceReceiverChanged}
          ></d-edit-text>

          <d-edit-text
            class="flexBasis600"
            label="${t('layout.subscription.organizationNumber')}"
            value="${this.editItem.invoiceOrganizationNumber}"
            placeholder="${t('layout.subscription.nineDigits')}"
            toolbar
            system-content
            @value-changed=${this.onInvoiceOrganizationNumberChanged}
          ></d-edit-text>

          <d-edit-textarea
            class="flexBasis600"
            label="${t('layout.subscription.invoiceAddress')}"
            value="${this.editItem.invoiceAddress}"
            toolbar
            system-content
            @value-changed=${this.onInvoiceAddressChanged}
          ></d-edit-textarea>

          <d-wrap>
            <d-edit-text
              class="flexBasis300"
              label="${t('layout.subscription.postcode')}"
              value="${this.editItem.invoicePostcode}"
              toolbar
              system-content
              @value-changed=${this.onInvoicePostcodeChanged}
            ></d-edit-text>

            <d-edit-text
              class="flexBasis300"
              label="${t('layout.subscription.locality')}"
              value="${this.editItem.invoiceLocality}"
              toolbar
              system-content
              @value-changed=${this.onInvoiceLocalityChanged}
            ></d-edit-text>
          </d-wrap>
          <d-edit-text
            class="flexBasis600"
            label="${t('layout.subscription.invoiceReference')}"
            value="${this.editItem.invoiceReference}"
            toolbar
            system-content
            @value-changed=${this.onInvoiceReferenceChanged}
          ></d-edit-text>
          <d-select-radio
            label="${t('layout.subscription.invoiceType')}"
            option-direction="horizontal"
            .options="${this.subscriptionTypeOptions}"
            .value="${this.editItem.invoiceSendMethod}"
            toolbar
            outskirts
            system-content
            @value-changed=${this.onInvoiceSendMethodChanged}
          >
          </d-select-radio>
        </d-wrap>
        <div class="bottom-right-buttons">
          <div class="action" @click=${this.onUpdateSubscription}>${t('layout.subscription.update')}</div>
        </div>
      </div>

      <div class="settings-section">
        <h2>${t('layout.subscription.userAgreementHeading')}</h2>
        <p>
          ${t('layout.subscription.termsOfUse')}
          <a class="textLink" href="https://trinnvis.no/om-trinnvis/brukeravtale/" target="_blank"
            >${t('layout.subscription.userAgreementLink')}</a
          >
        </p>
      </div>

      <div class="settings-section">
        <h2>${t('layout.subscription.loginRequirementHeading')}</h2>
        <d-wrap split baseline>
          ${this.editItem.requiresSecureLogin
            ? html` <div>
                  <p>${t('layout.subscription.secureLoginRequired')}</p>
                </div>
                <div>
                  <div class="action" @click=${() => this._disconnectSecureLogin()}>
                    ${t('layout.subscription.turnOffRequirement')}
                  </div>
                </div>`
            : html` <div>
                  <p>${t('layout.subscription.secureLoginOptional')}</p>
                </div>
                <div>
                  <div class="action" @click=${() => this._connectSecureLogin()}>
                    ${t('layout.subscription.turnOnRequirement')}
                  </div>
                </div>`}
        </d-wrap>
      </div>
    `;
  }

  connectedCallback() {
    super.connectedCallback();
    this.editItem = { ...this.subscription, specialTerms: this.subscription.specialTerms ?? 'NONE' };
  }

  private validInvoiceOrganizationNumber() {
    return /\d{9}/.test(this.editItem.invoiceOrganizationNumber);
  }

  private onOwnerEmailChanged(e: CustomEvent<{ value: string }>) {
    e.stopPropagation();
    this.editItem = { ...this.editItem, ownerEmail: e.detail.value };
  }

  private onSystemVersionChanged(e: CustomEvent<{ value: string }>) {
    e.stopPropagation();
    this.editItem = { ...this.editItem, singleUser: e.detail.value === 'singleUser' };
  }

  private onSpecialTermsChanged(e: CustomEvent<{ value: string }>) {
    e.stopPropagation();
    this.editItem = { ...this.editItem, specialTerms: e.detail.value };
  }

  private onInvoiceSendMethodChanged(e: CustomEvent<{ value: string }>) {
    e.stopPropagation();
    this.editItem = { ...this.editItem, invoiceSendMethod: e.detail.value === 'EHF' ? 'EHF' : 'MANUAL' };
  }

  private onInvoiceOrganizationNumberChanged(e: CustomEvent<{ value: string }>) {
    e.stopPropagation();
    this.editItem = { ...this.editItem, invoiceOrganizationNumber: e.detail.value.trim() };
  }

  private onInvoiceReceiverChanged(e: CustomEvent<{ value: string }>) {
    e.stopPropagation();
    this.editItem = { ...this.editItem, invoiceReceiver: e.detail.value };
  }

  private onInvoiceAddressChanged(e: CustomEvent<{ value: string }>) {
    e.stopPropagation();
    this.editItem = { ...this.editItem, invoiceAddress: e.detail.value };
  }

  private onInvoicePostcodeChanged(e: CustomEvent<{ value: string }>) {
    e.stopPropagation();
    this.editItem = { ...this.editItem, invoicePostcode: e.detail.value };
  }

  private onInvoiceLocalityChanged(e: CustomEvent<{ value: string }>) {
    e.stopPropagation();
    this.editItem = { ...this.editItem, invoiceLocality: e.detail.value };
  }

  private onInvoiceReferenceChanged(e: CustomEvent<{ value: string }>) {
    e.stopPropagation();
    this.editItem = { ...this.editItem, invoiceReference: e.detail.value };
  }

  private _disconnectSecureLogin() {
    this.dispatchEvent(new CustomEvent('disconnect-secure-login', { composed: true, bubbles: true }));
  }

  private _connectSecureLogin() {
    this.dispatchEvent(new CustomEvent('connect-secure-login', { composed: true, bubbles: true }));
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-organization-subscription': DOrganizationSubscription;
  }
}
